import React from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface FeaturedPostData {
  id: string;
  title: string;
  excerpt: string;
  category: string;
  author: string;
  date: string;
  readTime: string;
  image: string;
  alt: string;
  slug: string;
}

interface FeaturedPostProps {
  post: FeaturedPostData;
}

const FeaturedPost: React.FC<FeaturedPostProps> = ({ post }) => {
  return (
    <article className="bg-card rounded-lg overflow-hidden shadow-brand-elevated mb-12">
      <Link href={`/blog/${post.slug}`} className="block group">
        <div className="grid md:grid-cols-2 gap-0">
          <div className="relative h-64 md:h-full overflow-hidden">
            <AppImage
              src={post.image}
              alt={post.alt}
              className="w-full h-full object-cover group-hover:scale-105 transition-brand-complex"
            />
            <div className="absolute top-4 left-4">
              <span className="inline-flex items-center px-4 py-2 bg-accent text-accent-foreground text-sm font-body font-semibold rounded-full shadow-cta-glow">
                <Icon name="StarIcon" size={16} className="mr-2" variant="solid" />
                Featured Article
              </span>
            </div>
          </div>

          <div className="p-8 md:p-10 flex flex-col justify-center">
            <span className="inline-block px-3 py-1 bg-primary/10 text-primary text-sm font-body font-medium rounded-full mb-4 w-fit">
              {post.category}
            </span>

            <h2 className="font-headline font-bold text-3xl md:text-4xl text-foreground mb-4 group-hover:text-primary transition-brand">
              {post.title}
            </h2>

            <p className="font-body text-lg text-muted-foreground mb-6 leading-relaxed">
              {post.excerpt}
            </p>

            <div className="flex items-center justify-between text-sm text-muted-foreground font-body pt-4 border-t border-border">
              <div className="flex items-center space-x-4">
                <span className="flex items-center">
                  <Icon name="UserIcon" size={18} className="mr-2" />
                  {post.author}
                </span>
                <span className="flex items-center">
                  <Icon name="ClockIcon" size={18} className="mr-2" />
                  {post.readTime}
                </span>
              </div>
              <span className="flex items-center">
                <Icon name="CalendarIcon" size={18} className="mr-2" />
                {post.date}
              </span>
            </div>
          </div>
        </div>
      </Link>
    </article>
  );
};

export default FeaturedPost;